import * as RD from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';

interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible title — visually hidden, announced to screen readers. */
  title: string;
  children: React.ReactNode;
}

export function MobileDrawer({ open, onOpenChange, title, children }: MobileDrawerProps) {
  const path = useRouterState({ select: (s) => s.location.pathname });

  // Auto-close on navigation so a tap-through nav-item doesn't leave the
  // drawer covering the page it routed to.
  useEffect(() => {
    if (open) onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <RD.Portal forceMount>
            <RD.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden"
              />
            </RD.Overlay>
            <RD.Content asChild>
              <motion.div
                initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                className="fixed inset-y-0 left-0 z-50 w-[280px] bg-canvas border-r border-hairline flex flex-col lg:hidden"
              >
                <RD.Title className="sr-only">{title}</RD.Title>
                {children}
              </motion.div>
            </RD.Content>
          </RD.Portal>
        )}
      </AnimatePresence>
    </RD.Root>
  );
}
